interface SimpleDBConfig {
    dbName: string
    storeName: string
    version: number
}

class IndexDbWrapper {
    private dbName: string
    private storeName: string
    private version: number

    private static readonly sessionCacheIndexDbName = "SessionCacheDataDB"

    constructor(config?: Partial<SimpleDBConfig>) {
        this.dbName = config?.dbName || IndexDbWrapper.sessionCacheIndexDbName
        this.storeName = config?.storeName || "cache"
        this.version = config?.version || 1
    }

    private async open(): Promise<IDBDatabase> {
        return new Promise((resolve, reject) => {
            const request = indexedDB.open(this.dbName, this.version)

            request.onerror = () => reject(request.error)
            request.onsuccess = () => resolve(request.result)

            request.onupgradeneeded = (e) => {
                const db = (e.target as IDBOpenDBRequest).result
                if (!db.objectStoreNames.contains(this.storeName)) {
                    db.createObjectStore(this.storeName)
                }
            }
        })
    }

    async set<T>(key: string, value: T): Promise<T> {
        try {
            const db = await this.open()
            return new Promise((resolve, reject) => {
                const tx = db.transaction(this.storeName, "readwrite")
                const store = tx.objectStore(this.storeName)
                const request = store.put(JSON.stringify(value), key)

                request.onsuccess = () => resolve(value)
                request.onerror = () => reject(request.error)
            })
        } catch (error) {
            console.error("IndexedDB set error:", error)
            throw error
        }
    }

    async get<T>(key: string): Promise<T | undefined> {
        try {
            const db = await this.open()
            return new Promise((resolve, reject) => {
                const tx = db.transaction(this.storeName, "readonly")
                const store = tx.objectStore(this.storeName)
                const request = store.get(key)

                request.onsuccess = () => resolve(request.result)
                request.onerror = () => reject(request.error)
            })
        } catch (error) {
            console.error("IndexedDB get error:", error)
            throw error
        }
    }

    async delete(key: string): Promise<boolean> {
        try {
            const db = await this.open()
            return new Promise((resolve, reject) => {
                const tx = db.transaction(this.storeName, "readwrite")
                const store = tx.objectStore(this.storeName)
                const request = store.delete(key)

                request.onsuccess = () => resolve(true)
                request.onerror = () => reject(request.error)
            })
        } catch (error) {
            console.error("IndexedDB delete error:", error)
            throw error
        }
    }

    async clear(): Promise<boolean> {
        try {
            const db = await this.open()
            return new Promise((resolve, reject) => {
                const tx = db.transaction(this.storeName, "readwrite")
                const store = tx.objectStore(this.storeName)
                const request = store.clear()

                request.onsuccess = () => resolve(true)
                request.onerror = () => reject(request.error)
            })
        } catch (error) {
            console.error("IndexedDB clear error:", error)
            throw error
        }
    }

    async keys(): Promise<IDBValidKey[]> {
        try {
            const db = await this.open()
            return new Promise((resolve, reject) => {
                const tx = db.transaction(this.storeName, "readonly")
                const store = tx.objectStore(this.storeName)
                const request = store.getAllKeys()

                request.onsuccess = () => resolve(request.result)
                request.onerror = () => reject(request.error)
            })
        } catch (error) {
            console.error("IndexedDB keys error:", error)
            throw error
        }
    }

    async has(key: string): Promise<boolean> {
        const value = await this.get(key)
        return value !== undefined
    }
}

// Create a singleton instance
export const db = new IndexDbWrapper()

// Export the class if you need multiple instances
export default IndexDbWrapper
